import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { levelnoApi } from "../../http/http";
import { isFresh } from "../cacheUtils";

const TTL_MS = 1000 * 60 * 5; // 5 min per (level_no, lang)

const makeLevelKey = (level_no, lang) => `${level_no}|${lang}`;

export const fetchLevel = createAsyncThunk(
  "game/fetchLevel",
  async ({ token, level_no, lang }, thunkAPI) => {
    if (!token) throw new Error("Missing auth token");
    const res = await levelnoApi({ token, level_no, lang });
    return res?.data || null;
  },
  {
    condition: ({ token, level_no, lang }, { getState }) => {
      const state = getState().game;
      if (!token) return false;
      const key = makeLevelKey(level_no, lang);
      const cached = state.byKey[key];
      if (!cached?.data) return true;
      return !isFresh(cached.lastFetched, TTL_MS);
    },
  }
);

const gameSlice = createSlice({
  name: "game",
  initialState: {
    byKey: {},
  },
  reducers: {
    clearGame() {
      return { byKey: {} };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLevel.pending, (state, action) => {
        const { level_no, lang } = action.meta.arg;
        const key = makeLevelKey(level_no, lang);
        state.byKey[key] = state.byKey[key] || {};
        state.byKey[key].status = "loading";
        state.byKey[key].error = null;
      })
      .addCase(fetchLevel.fulfilled, (state, action) => {
        const { level_no, lang } = action.meta.arg;
        const key = makeLevelKey(level_no, lang);
        state.byKey[key] = state.byKey[key] || {};
        state.byKey[key].status = "succeeded";
        state.byKey[key].data = action.payload;
        state.byKey[key].lastFetched = Date.now();
        state.byKey[key].error = null;
      })
      .addCase(fetchLevel.rejected, (state, action) => {
        const { level_no, lang } = action.meta.arg || {};
        const key = makeLevelKey(level_no, lang);
        state.byKey[key] = state.byKey[key] || {};
        state.byKey[key].status = "failed";
        state.byKey[key].error = action.error?.message || "Failed to load level";
      });
  },
});

export const { clearGame } = gameSlice.actions;
export default gameSlice.reducer;

export const makeLevelKeySelector = (level_no, lang) => makeLevelKey(level_no, lang);

